import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "../../common/dropDown";
import GenderSelector from "../../common/GenderSelector";
import SimpleCircularUpload from "../../common/SimpleCircularUpload";
import StudyAssignmentDualList, {
  mapStudyOptionsToAssignedRows,
} from "./StudyAssignmentDualList";
import UserRolePermissionPanel from "./UserRolePermissionPanel";
import UserFormSection from "./UserFormSection";
import UserFormCollapsible from "./UserFormCollapsible";
import AdditionalTrainingRolesLabel from "../../common/AdditionalTrainingRolesLabel";
import StageStepper from "../../common/StageStepper/StageStepper";
import { organizationHasStudyManagementModule } from "../../../utils/orgModuleAccess";
import { isSponsorCroUserRecord } from "../../../constants/userTypeLabels";
import {
  normalizeNationalMobileInput,
  orgMobileDialLabel,
  validateNationalMobile,
} from "../../../utils/siteFieldValidationUtils";

/** Modal steps — CAPA-style chevron: User Info (full form) → Study Info (optional). */
const STEP_USER_INFO = "userInfo";
const STEP_STUDY_INFO = "studyInfo";

const USER_INFO_ERROR_FIELDS = [
  "employeeNumber",
  "firstName",
  "lastName",
  "email",
  "confirmEmail",
  "contactNumber",
  "selectedSites",
  "role",
];
const STUDY_INFO_ERROR_FIELDS = ["studies"];

function normalizeRoleLabel(label) {
  return String(label ?? "")
    .trim()
    .toLowerCase();
}

function isSystemAdministratorRole(role) {
  const label =
    typeof role === "object" && role !== null ? role.label : role ?? "";
  return normalizeRoleLabel(label) === "system administrator";
}

const ROLE_PERMISSION_INFO =
  "Permissions are saved on this user when you click Add or Update. The role is used for reporting only; access comes from the selections below.";

const ROLE_INFO_POPOVER_WIDTH = 288;
const ROLE_INFO_POPOVER_Z_INDEX = 10000001;

function RolePermissionInfoPopover() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const helpId = "role-permission-info-popover";
  const [style, setStyle] = useState({
    top: 0,
    left: 0,
    maxWidth: ROLE_INFO_POPOVER_WIDTH,
    transform: "translateY(0)",
  });

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const showPopover = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const hidePopoverSoon = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const popoverWidth = Math.min(
      ROLE_INFO_POPOVER_WIDTH,
      window.innerWidth - 16,
    );
    const popoverHeight = popoverRef.current?.offsetHeight || 72;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const showAbove =
      spaceBelow < popoverHeight + 6 && spaceAbove >= spaceBelow;

    let left = rect.right - popoverWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverWidth - 8));

    const top = showAbove ? rect.top - 6 : rect.bottom + 6;

    setStyle({
      top,
      left,
      maxWidth: popoverWidth,
      transform: showAbove ? "translateY(-100%)" : "translateY(0)",
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (open && popoverRef.current) {
      updatePosition();
    }
  }, [open, updatePosition]);

  return (
    <div
      className={`user-modal-fold__info${open ? " is-open" : ""}`}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopoverSoon}
    >
      <button
        ref={triggerRef}
        type="button"
        className="user-modal-fold__info-btn"
        aria-label="Role and permissions information"
        aria-expanded={open}
        aria-controls={helpId}
        onClick={() => setOpen((current) => !current)}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              id={helpId}
              className="user-modal-fold__info-popover user-modal-fold__info-popover--portal"
              role="tooltip"
              style={{
                position: "fixed",
                top: style.top,
                left: style.left,
                maxWidth: style.maxWidth,
                transform: style.transform,
                zIndex: ROLE_INFO_POPOVER_Z_INDEX,
              }}
              onMouseEnter={showPopover}
              onMouseLeave={hidePopoverSoon}
            >
              <p className="user-modal-fold__info-text">{ROLE_PERMISSION_INFO}</p>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * @param {"modal"|"page"} variant
 *   modal — Add User popup
 *   page  — inline on User Profile (View)
 */
function InternalUserAddEditModal({
  open = true,
  variant = "modal",
  formData,
  fieldErrors = {},
  roles,
  visibleRoles,
  department,
  siteOptions,
  study,
  maxDate,
  userData,
  canEditUserPermissions,
  canEditRole = false,
  canEditAdminSections = false,
  onClose,
  onSave,
  onSaveAdditionalInfo,
  onSaveStudies,
  onSaveRolePermissions,
  /** Profile: Studies live on a separate tab */
  hideStudiesSection = false,
  /** Sponsor/CRO own profile: role is on the identity card; no permission matrix. */
  hideRolePermissionSection = false,
  /** Profile: per-module PermissionCards; Add User keeps the tree */
  permissionLayout = "tree",
  onFormInput,
  onDobChange,
  onStartDateChange,
  onAccessDateChange,
  onGenderChange,
  onRoleSelect,
  onUserPermissionsChange,
  onDepartmentSelect,
  onSitesSelect,
  onProfilePictureChange,
  onStudiesChange,
  showQmsTrainingAdditionalRoles = false,
  qmsTrainingAdditionalRoleOptions = [],
  onQmsTrainingAdditionalRolesSelect,
  saveLabel,
  /** Profile page: render left identity column inside the form panel */
  identityColumn = null,
  /** Profile page with external sidebar — hide photo/name/contact/gender fields */
  hideBasicIdentityFields = false,
}) {
  const [sectionExpandPopup, setSectionExpandPopup] = useState(null);
  const [modalStepId, setModalStepId] = useState(STEP_USER_INFO);
  const [stepFieldErrors, setStepFieldErrors] = useState({});
  const isPage = variant === "page";
  const showIdentityColumn = isPage && Boolean(identityColumn);
  const hideBasicFields = showIdentityColumn || (isPage && hideBasicIdentityFields);
  const isExternalUser = isSponsorCroUserRecord(formData?.IsCRO);

  /** Step 2 only when org has Study Management — otherwise single User Info screen. */
  const showStudiesStep =
    !isPage &&
    !hideStudiesSection &&
    organizationHasStudyManagementModule(userData);

  const modalStageSteps = useMemo(
    () => [
      { id: STEP_USER_INFO, label: "User Info", icon: "user" },
      { id: STEP_STUDY_INFO, label: "Study Info", icon: "flask" },
    ],
    [],
  );

  const mergedFieldErrors = useMemo(
    () => ({ ...fieldErrors, ...stepFieldErrors }),
    [fieldErrors, stepFieldErrors],
  );

  const fieldError = (key) => {
    const message = mergedFieldErrors?.[key];
    if (!message) return null;
    return <div className="user-form-field-error">{message}</div>;
  };

  useEffect(() => {
    if (!open) {
      setSectionExpandPopup(null);
      setModalStepId(STEP_USER_INFO);
      setStepFieldErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (isPage || !open) return;
    if (!showStudiesStep && modalStepId === STEP_STUDY_INFO) {
      setModalStepId(STEP_USER_INFO);
    }
  }, [isPage, open, showStudiesStep, modalStepId]);

  // When parent save validation fails, jump to the step that owns the error.
  useEffect(() => {
    if (isPage || !open || !fieldErrors || typeof fieldErrors !== "object") {
      return;
    }
    const keys = Object.keys(fieldErrors);
    if (!keys.length) return;
    if (
      showStudiesStep &&
      keys.some((key) => STUDY_INFO_ERROR_FIELDS.includes(key)) &&
      !keys.some((key) => USER_INFO_ERROR_FIELDS.includes(key))
    ) {
      setModalStepId(STEP_STUDY_INFO);
      return;
    }
    if (keys.some((key) => USER_INFO_ERROR_FIELDS.includes(key))) {
      setModalStepId(STEP_USER_INFO);
    }
  }, [fieldErrors, isPage, open, showStudiesStep]);

  if (!open) return null;

  const isEditing = Boolean(formData.StaffId);
  const isSysAdminRole = isSystemAdministratorRole(formData.role);
  const showSitesField = !isSysAdminRole && !isExternalUser;
  const hasAddressData = Boolean(
    String(formData.addressLine1 || "").trim() ||
      String(formData.postalCode || "").trim() ||
      String(formData.city || "").trim() ||
      String(formData.county || "").trim() ||
      String(formData.country || "").trim(),
  );

  const selectedStageIndex =
    modalStepId === STEP_STUDY_INFO && showStudiesStep ? 1 : 0;
  const isOnStudyStep = selectedStageIndex === 1;

  /** Study step is optional — only enforce User Info rules on Save/Add. */
  const validateUserInfoStep = () => {
    const errors = {};
    const trim = (value) => String(value ?? "").trim();

    if (!trim(formData.firstName)) errors.firstName = "First name is required.";
    if (!trim(formData.lastName)) errors.lastName = "Last name is required.";
    const mobileCountryIso2 = String(
      formData.mobileCountryIso2 || "GB",
    )
      .trim()
      .toUpperCase();
    const contactResult = validateNationalMobile(formData.contactNumber, {
      countryIso2: mobileCountryIso2,
      required: false,
    });
    if (!contactResult.valid) {
      errors.contactNumber = contactResult.message;
    }
    const empNo = trim(formData.employeeNumber);
    if (!empNo || empNo === "00000") {
      errors.employeeNumber = "Employee number is required.";
    }
    const email = trim(formData.email);
    if (!email) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    const confirm = trim(formData.confirmEmail);
    if (email && !confirm) {
      errors.confirmEmail = "Confirm email is required.";
    } else if (
      email &&
      confirm &&
      email.toLowerCase() !== confirm.toLowerCase()
    ) {
      errors.confirmEmail = "Email and confirm email must match.";
    }

    if (showSitesField) {
      const selected = formData.selectedSites || [];
      if (!Array.isArray(selected) || selected.length === 0) {
        errors.selectedSites = "At least one site is required.";
      }
    }

    const roleId =
      typeof formData.role === "object" && formData.role !== null
        ? formData.role.value ?? formData.role.RoleId ?? formData.role.id
        : formData.role;
    if (!String(roleId ?? "").trim()) {
      errors.role = "Role is required.";
    }

    return errors;
  };

  // Green "complete" only when User Info is actually valid — not just because we left the step.
  const isUserInfoStepComplete =
    Object.keys(validateUserInfoStep()).length === 0;

  // Stepper / Next / Back — no validation (Study Info is optional).
  const handleModalNext = () => {
    if (!showStudiesStep) return;
    setStepFieldErrors({});
    setModalStepId(STEP_STUDY_INFO);
  };

  const handleModalBack = () => {
    setStepFieldErrors({});
    setModalStepId(STEP_USER_INFO);
  };

  const handleModalStageClick = (index) => {
    if (index === selectedStageIndex) return;
    if (index === 0) {
      handleModalBack();
      return;
    }
    if (index === 1 && showStudiesStep) {
      handleModalNext();
    }
  };

  const handleModalSave = () => {
    const errors = validateUserInfoStep();
    if (Object.keys(errors).length > 0) {
      setStepFieldErrors(errors);
      setModalStepId(STEP_USER_INFO);
      return;
    }
    setStepFieldErrors({});
    onSave?.();
  };

  const openSectionExpandPopup = (section) => {
    setSectionExpandPopup(section);
  };

  const closeSectionExpandPopup = () => {
    setSectionExpandPopup(null);
  };

  const showProfileSectionSaves =
    isPage && hideBasicFields && canEditAdminSections;
  const lockAdminOrgFields = isPage && !canEditAdminSections;

  const renderSectionUpdateButton = (onClick) => {
    if (!showProfileSectionSaves || typeof onClick !== "function") return null;
    return (
      <button
        type="button"
        className="btn btn-primary user-modal-fold__update-btn"
        onClick={onClick}
      >
        <i className="fas fa-save" aria-hidden="true" />
        Update
      </button>
    );
  };

  const renderStudiesPicker = () => (
    <StudyAssignmentDualList
      options={study}
      selected={
        isSysAdminRole
          ? mapStudyOptionsToAssignedRows(study)
          : formData.studies || []
      }
      onChange={onStudiesChange}
      readOnly={isSysAdminRole}
      readOnlyMessage="System Administrator has access to all studies. Changes are not saved — access is automatic."
    />
  );

  const roleInModuleSide = permissionLayout === "modules";

  const joinDepartmentWithRole = Boolean(
    isPage && hideBasicFields && isExternalUser && canEditAdminSections,
  );

  const renderRolePermissionPanel = () => (
    <UserRolePermissionPanel
      role={formData.role}
      roles={roles}
      roleOptions={visibleRoles}
      onRoleSelect={onRoleSelect}
      userPermissions={formData.userPermissions || []}
      onUserPermissionsChange={onUserPermissionsChange}
      userData={userData}
      canEdit={canEditUserPermissions}
      canEditRole={canEditRole}
      layout={permissionLayout}
      hideRoleDropdown={false}
      onSavePermissions={onSaveRolePermissions}
      compact
      showQmsTrainingAdditionalRoles={false}
      qmsTrainingAdditionalRoleOptions={qmsTrainingAdditionalRoleOptions}
      qmsTrainingAdditionalRoles={formData.qmsTrainingAdditionalRoles || []}
      onQmsTrainingAdditionalRolesSelect={onQmsTrainingAdditionalRolesSelect}
      showDepartment={joinDepartmentWithRole}
      departmentOptions={department}
      selectedDepartment={formData.department}
      onDepartmentSelect={onDepartmentSelect}
      departmentDisabled={lockAdminOrgFields}
    />
  );

  const rolePermissionSectionTitle = (
    <span className="user-modal-fold__title-group">
      Role & Permission <span className="mandatory">*</span>
      <RolePermissionInfoPopover />
    </span>
  );

  const sectionExpandModal =
    sectionExpandPopup && typeof document !== "undefined"
      ? createPortal(
          <div
            className="modal fade show d-block user-section-expand-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-section-expand-title"
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable user-section-expand-modal__dialog">
              <div className="modal-content user-section-expand-modal__content">
                <div className="modal-header">
                  <h5 className="modal-title" id="user-section-expand-title">
                    {sectionExpandPopup === "studies" ? (
                      "Studies"
                    ) : (
                      <>
                        Role & Permission <span className="mandatory">*</span>
                      </>
                    )}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSectionExpandPopup}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body user-section-expand-modal__body">
                  {sectionExpandPopup === "studies"
                    ? renderStudiesPicker()
                    : renderRolePermissionPanel()}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={closeSectionExpandPopup}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const panelClassName = [
    "user-modal-panel",
    "user-modal-panel--four-col",
    showIdentityColumn ? "user-modal-panel--with-identity" : "",
    hideBasicFields && !isExternalUser ? "user-modal-panel--profile-additional" : "",
    hideBasicFields && isExternalUser ? "user-modal-panel--cro-profile" : "",
    hideStudiesSection || (!isPage && !isOnStudyStep)
      ? "user-modal-panel--no-studies"
      : "",
    !isPage && isOnStudyStep ? "user-modal-panel--study-step" : "",
    permissionLayout === "modules" ? "user-modal-panel--permission-modules" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const userInformationSection = (
      <UserFormSection
        title={hideBasicFields ? "Additional" : "User Information"}
        className={[
          "user-modal-fold--user-col-a",
          hideBasicFields ? "user-modal-fold--additional" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        footer={renderSectionUpdateButton(onSaveAdditionalInfo)}
      >
        <div
          className={[
            "user-form-grid",
            hideBasicFields ? "user-form-grid--single" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {hideBasicFields &&
          (mergedFieldErrors.employeeNumber ||
            mergedFieldErrors.firstName ||
            mergedFieldErrors.lastName ||
            mergedFieldErrors.contactNumber ||
            mergedFieldErrors.email ||
            mergedFieldErrors.confirmEmail) ? (
            <div className="user-form-field user-form-field--span-row">
              {fieldError("employeeNumber")}
              {fieldError("firstName")}
              {fieldError("lastName")}
              {fieldError("contactNumber")}
              {fieldError("email")}
              {fieldError("confirmEmail")}
            </div>
          ) : null}
          {!hideBasicFields ? (
            <>
              <div className="user-form-field user-form-field--photo">
                <SimpleCircularUpload
                  currentImage={formData.profilePicture}
                  size={72}
                  maxFileSize={5}
                  onChange={onProfilePictureChange}
                />
              </div>
              <div className="user-form-field">
                <label>
                  Employee Number <span className="mandatory">*</span>
                </label>
                <div className="span-under-input">
                  <span>{formData.prefix}</span>
                  <input
                    type="text"
                    className={`form-control${mergedFieldErrors.employeeNumber ? " is-invalid" : ""}`}
                    name="employeeNumber"
                    value={formData.employeeNumber || ""}
                    onChange={onFormInput}
                  />
                </div>
                {fieldError("employeeNumber")}
              </div>
              <div className="user-form-field">
                <label>
                  First Name <span className="mandatory">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${mergedFieldErrors.firstName ? " is-invalid" : ""}`}
                  name="firstName"
                  value={formData.firstName || ""}
                  onChange={onFormInput}
                />
                {fieldError("firstName")}
              </div>
              <div className="user-form-field">
                <label>
                  Last Name <span className="mandatory">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${mergedFieldErrors.lastName ? " is-invalid" : ""}`}
                  name="lastName"
                  value={formData.lastName || ""}
                  onChange={onFormInput}
                />
                {fieldError("lastName")}
              </div>
              <div className="user-form-field">
                <label>DOB</label>
                <DatePicker
                  selected={formData.dob}
                  onChange={onDobChange}
                  placeholderText="Select date"
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  onChangeRaw={(event) => event.preventDefault()}
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
              <div className="user-form-field">
                <label>
                  Email <span className="mandatory">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${mergedFieldErrors.email ? " is-invalid" : ""}`}
                  name="email"
                  value={formData.email || ""}
                  onChange={onFormInput}
                />
                {fieldError("email")}
              </div>
              <div className="user-form-field">
                <label>
                  Confirm Email <span className="mandatory">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${mergedFieldErrors.confirmEmail ? " is-invalid" : ""}`}
                  name="confirmEmail"
                  value={formData.confirmEmail || ""}
                  onChange={onFormInput}
                />
                {fieldError("confirmEmail")}
              </div>
              <div className="user-form-field">
                <label>
                  Contact Number
                </label>
                <div className="um-phone-input">
                  <select
                    className="form-control um-phone-input__dial"
                    value={orgMobileDialLabel(
                      formData.mobileCountryIso2 || "GB",
                    )}
                    disabled
                    aria-label="Organisation country dial code"
                  >
                    <option
                      value={orgMobileDialLabel(
                        formData.mobileCountryIso2 || "GB",
                      )}
                    >
                      {orgMobileDialLabel(formData.mobileCountryIso2 || "GB")}
                    </option>
                  </select>
                  <input
                    type="text"
                    className={`form-control${mergedFieldErrors.contactNumber ? " is-invalid" : ""}`}
                    name="contactNumber"
                    value={formData.contactNumber || ""}
                    onChange={(event) => {
                      const iso = formData.mobileCountryIso2 || "GB";
                      const { national, wrongDial } =
                        normalizeNationalMobileInput(event.target.value, iso);
                      onFormInput({
                        target: { name: "contactNumber", value: national },
                      });
                      setStepFieldErrors((prev) => {
                        const next = { ...prev };
                        if (wrongDial) {
                          next.contactNumber = `Contact number must use the organisation country dial code (${orgMobileDialLabel(iso)}).`;
                        } else {
                          delete next.contactNumber;
                        }
                        return next;
                      });
                    }}
                    onPaste={(event) => {
                      const text =
                        event.clipboardData?.getData("text") ?? "";
                      event.preventDefault();
                      const iso = formData.mobileCountryIso2 || "GB";
                      const { national, wrongDial } =
                        normalizeNationalMobileInput(text, iso);
                      onFormInput({
                        target: { name: "contactNumber", value: national },
                      });
                      setStepFieldErrors((prev) => {
                        const next = { ...prev };
                        if (wrongDial) {
                          next.contactNumber = `Contact number must use the organisation country dial code (${orgMobileDialLabel(iso)}).`;
                        } else {
                          delete next.contactNumber;
                        }
                        return next;
                      });
                    }}
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="7123456789"
                  />
                </div>
                {fieldError("contactNumber")}
              </div>
              <div className="user-form-field">
                <label>Gender</label>
                <GenderSelector
                  value={formData.gender}
                  onChange={onGenderChange}
                />
              </div>
              <UserFormCollapsible
                title="Address"
                defaultOpen={hasAddressData}
                className="user-form-field--span-row"
              >
                <div className="user-form-grid user-form-grid--address">
                  <div className="user-form-field user-form-field--address-line">
                    <label>Address</label>
                    <textarea
                      className="form-control"
                      name="addressLine1"
                      value={formData.addressLine1 || ""}
                      onChange={onFormInput}
                      rows={2}
                    />
                  </div>
                  <div className="user-form-field">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="postalCode"
                      value={formData.postalCode || ""}
                      onChange={onFormInput}
                    />
                  </div>
                  <div className="user-form-field">
                    <label>City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={formData.city || ""}
                      onChange={onFormInput}
                    />
                  </div>
                  <div className="user-form-field">
                    <label>County</label>
                    <input
                      type="text"
                      className="form-control"
                      name="county"
                      value={formData.county || ""}
                      onChange={onFormInput}
                    />
                  </div>
                  <div className="user-form-field">
                    <label>Country</label>
                    <input
                      type="text"
                      className="form-control"
                      name="country"
                      value={formData.country || ""}
                      onChange={onFormInput}
                    />
                  </div>
                </div>
              </UserFormCollapsible>
            </>
          ) : (
            <>
              <div className="user-form-field">
                <label>Department</label>
                <Dropdown
                  options={department}
                  isMulti={false}
                  selected={formData.department}
                  onSelect={onDepartmentSelect}
                  disabled={lockAdminOrgFields}
                />
              </div>
              {showSitesField ? (
                <div className="user-form-field">
                  <label>
                    Sites <span className="mandatory">*</span>
                  </label>
                  <Dropdown
                    options={siteOptions}
                    isMulti={true}
                    selected={formData.selectedSites || []}
                    onSelect={onSitesSelect}
                    showMultiSelectedTooltip
                    disabled={lockAdminOrgFields}
                  />
                  {fieldError("selectedSites")}
                </div>
              ) : null}
              {showQmsTrainingAdditionalRoles &&
              onQmsTrainingAdditionalRolesSelect ? (
                <div className="user-form-field">
                  <AdditionalTrainingRolesLabel />
                  <Dropdown
                    options={qmsTrainingAdditionalRoleOptions}
                    isMulti={true}
                    selected={formData.qmsTrainingAdditionalRoles || []}
                    onSelect={onQmsTrainingAdditionalRolesSelect}
                    showMultiSelectedTooltip
                    disabled={lockAdminOrgFields}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </UserFormSection>
  );

  const additionalInfoSection = !hideBasicFields ? (
    <UserFormSection
      title="Additional Info"
      className="user-modal-fold--user-col-b"
    >
      <div className="user-form-grid user-form-grid--single">
        <div className="user-form-field">
          <label>Start Date</label>
          <DatePicker
            selected={formData.startDate}
            onChange={onStartDateChange}
            placeholderText="Select date"
            className="form-control"
            dateFormat="dd/MM/yyyy"
            isClearable
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={new Date()}
            maxDate={maxDate}
            onChangeRaw={(event) => event.preventDefault()}
            popperProps={{ strategy: "fixed" }}
          />
        </div>
        <div className="user-form-field">
          <label>Access Date</label>
          <DatePicker
            selected={formData.accessDate}
            onChange={onAccessDateChange}
            placeholderText="Select date"
            className="form-control"
            dateFormat="dd/MM/yyyy"
            isClearable
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={new Date()}
            maxDate={maxDate}
            onChangeRaw={(event) => event.preventDefault()}
            popperProps={{ strategy: "fixed" }}
          />
        </div>
        <div className="user-form-field">
          <label>Department</label>
          <Dropdown
            options={department}
            isMulti={false}
            selected={formData.department}
            onSelect={onDepartmentSelect}
            disabled={lockAdminOrgFields}
          />
        </div>
        {showSitesField ? (
          <div className="user-form-field">
            <label>
              Sites <span className="mandatory">*</span>
            </label>
            <Dropdown
              options={siteOptions}
              isMulti={true}
              selected={formData.selectedSites || []}
              onSelect={onSitesSelect}
              showMultiSelectedTooltip
              disabled={lockAdminOrgFields}
            />
            {fieldError("selectedSites")}
          </div>
        ) : null}
        {showQmsTrainingAdditionalRoles &&
        onQmsTrainingAdditionalRolesSelect ? (
          <div className="user-form-field">
            <AdditionalTrainingRolesLabel />
            <Dropdown
              options={qmsTrainingAdditionalRoleOptions}
              isMulti={true}
              selected={formData.qmsTrainingAdditionalRoles || []}
              onSelect={onQmsTrainingAdditionalRolesSelect}
              showMultiSelectedTooltip
              disabled={lockAdminOrgFields}
            />
          </div>
        ) : null}
      </div>
    </UserFormSection>
  ) : null;

  const studiesSectionTitle = (
    <>
      Studies
      {isExternalUser ? (
        <>
          {" "}
          <span className="mandatory">*</span>
        </>
      ) : !isPage ? (
        <>
          {" "}
          <span className="user-modal-fold__optional-tag">(optional)</span>
        </>
      ) : null}
    </>
  );

  const studiesSection =
    (isPage && !hideStudiesSection) || (!isPage && showStudiesStep) ? (
      <UserFormSection
        title={studiesSectionTitle}
        className={`user-modal-fold--studies${mergedFieldErrors.studies ? " has-field-error" : ""}`}
        onToggleMaximize={() => openSectionExpandPopup("studies")}
        footer={renderSectionUpdateButton(onSaveStudies)}
      >
        {renderStudiesPicker()}
        {fieldError("studies")}
      </UserFormSection>
    ) : null;

  const rolePermissionSection = (
    <UserFormSection
      title={rolePermissionSectionTitle}
      className={`user-modal-fold--permissions${mergedFieldErrors.role ? " has-field-error" : ""}`}
      onToggleMaximize={
        roleInModuleSide
          ? undefined
          : () => openSectionExpandPopup("permissions")
      }
      footer={renderSectionUpdateButton(onSaveRolePermissions)}
    >
      {renderRolePermissionPanel()}
      {fieldError("role")}
    </UserFormSection>
  );

  const pageFormBody = (
    <div className={panelClassName}>
      {showIdentityColumn ? (
        <UserFormSection
          hideHead
          className="user-modal-fold--identity user-modal-fold--no-head"
        >
          {identityColumn}
        </UserFormSection>
      ) : null}
      {joinDepartmentWithRole ? null : userInformationSection}
      {additionalInfoSection}
      {studiesSection}
      {hideRolePermissionSection ? null : rolePermissionSection}
    </div>
  );

  /** Step 1: original 3-column layout (User + Additional + Role). Step 2: Studies only. */
  const modalFormBody = (
    <div className={panelClassName}>
      {isOnStudyStep ? (
        studiesSection
      ) : (
        <>
          {userInformationSection}
          {additionalInfoSection}
          {rolePermissionSection}
        </>
      )}
    </div>
  );

  const saveButton = (
    <button type="button" className="btn btn-primary" onClick={handleModalSave}>
      {saveLabel ? (
        saveLabel
      ) : isEditing || isPage ? (
        <>
          <i className="fas fa-save me-2" aria-hidden="true" />
          Update
        </>
      ) : (
        <>
          <i className="fas fa-plus me-2" aria-hidden="true" />
          Add
        </>
      )}
    </button>
  );

  const footerActions = (
    <div className={isPage ? "user-profile-edit-footer" : "modal-footer"}>
      {!isPage && onClose ? (
        <button
          type="button"
          className="btn btn-outline-primary me-2"
          onClick={onClose}
        >
          <i className="fas fa-close me-2" aria-hidden="true" />
          Cancel
        </button>
      ) : null}
      {!isPage && isOnStudyStep ? (
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={handleModalBack}
        >
          <i className="fas fa-arrow-left me-2" aria-hidden="true" />
          Back
        </button>
      ) : null}
      {!isPage && showStudiesStep && !isOnStudyStep ? (
        <button
          type="button"
          className="btn btn-outline-primary me-2"
          onClick={handleModalNext}
        >
          Next
          <i className="fas fa-arrow-right ms-2" aria-hidden="true" />
        </button>
      ) : null}
      {saveButton}
    </div>
  );

  if (isPage) {
    return (
      <>
        <div className="user-add-edit-modal user-add-edit-modal--page">
          <div className="user-modal-body--sections">{pageFormBody}</div>
        </div>
        {sectionExpandModal}
      </>
    );
  }

  return createPortal(
    <>
      <div
        className="modal fade show d-block user-workspace-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="internal-user-add-edit-title"
      >
        <div className="modal-dialog user-add-edit-modal user-add-edit-modal--stepper">
          <div className="modal-content">
            <div className="modal-header user-add-edit-modal__header">
              <h5 className="modal-title" id="internal-user-add-edit-title">
                {isEditing
                  ? "Edit User"
                  : isExternalUser
                    ? "Add Sponsor/CRO User"
                    : "Add Internal User"}
              </h5>
              {showStudiesStep ? (
                <div className="user-add-edit-modal__header-stepper">
                  <StageStepper
                    steps={modalStageSteps}
                    selectedIndex={selectedStageIndex}
                    completedIndices={isUserInfoStepComplete ? [0] : []}
                    onStepClick={handleModalStageClick}
                    ariaLabel="Add user steps"
                  />
                </div>
              ) : null}
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            <div className="modal-body user-modal-body--sections user-modal-body--stepper">
              {modalFormBody}
            </div>
            {footerActions}
          </div>
        </div>
      </div>
      {sectionExpandModal}
    </>,
    document.body,
  );
}

export default InternalUserAddEditModal;
